/**
 * Correlation ids, minted per USER ACTION (API-STANDARD.md §4).
 *
 * The server mints one when a request arrives without it, but then a single
 * user action that fans out into four requests produces four unrelated log
 * threads. The client owns the id instead: one id per navigation or per
 * explicit action, sent on every request that action causes, shown to the user
 * in an error toast ("Reference id …") so support can find the exact log line.
 */

/** The id in force right now. Replaced by `beginUserAction`. */
let currentId: string | null = null;

function mintId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  // jsdom without randomUUID, or a very old browser. Shape-compatible only.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Start a new correlated action and return its id. Call this on navigation and
 * at the top of an explicit user action (save, run, retry) — not per request.
 */
export function beginUserAction(): string {
  currentId = mintId();
  return currentId;
}

/** The current action's id, minting one if nothing started an action yet. */
export function currentCorrelationId(): string {
  if (!currentId) currentId = mintId();
  return currentId;
}

/** Test seam: forget the current action so the next call mints a fresh id. */
export function resetCorrelation(): void {
  currentId = null;
}
